import React, { useState, useEffect } from 'react'
import {
  CContainer,
  CHeader,
  CHeaderNav,
  CHeaderToggler,
  CNavLink,
  CNavItem,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CDropdownHeader,
  CDropdownDivider,
  CBadge,
  CAvatar,
  CSpinner,
  CListGroup,
  CListGroupItem,
  CButtonGroup,
  CButton
} from '@coreui/react'
import { CIcon } from '@coreui/icons-react'
import {
  cilBell,
  cilUser,
  cilAccountLogout,
  cilSun,
  cilMoon,
  cilApplications,
  cilList,
  cilStar,
  cilLockLocked,
  cilClock,
  cilCheckCircle
} from '@coreui/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import DynamicBreadcrumb from './DynamicBreadcrumb'
import { useAuth } from '../utils/AuthContext'
import { useTheme } from '../utils/ThemeContext'
import { getUnreadCount, fetchNotifications, markNotificationAsRead, markNotificationAsUnread, markAllNotificationsAsRead, markAllNotificationsAsUnread } from '../../utils/api'

const MembersHeader = () => {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false)
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    console.log('User data from AuthContext:', user)
    console.log('User data keys:', user ? Object.keys(user) : 'No user')
    console.log('User firstName:', user?.firstName)
    console.log('User lastName:', user?.lastName)
    console.log('User full_name:', user?.full_name)
    console.log('User email:', user?.email)
    console.log('User id:', user?.id)

    // Load unread notification count
    if (user?.id) {
      console.log('Loading initial unread count for user:', user.id)
      loadUnreadCount(user.id)
    } else {
      console.log('No user ID found, skipping notification loading')
    }
  }, [user])



  const loadUnreadCount = async (userId) => {
    try {
      console.log('Loading unread count for user:', userId)
      const response = await getUnreadCount(userId)
      console.log('Unread count response:', response)

      if (response.status === 'success') {
        const count = response.data.count
        console.log('Setting unread count:', count)
        setUnreadCount(count)
      } else {
        console.error('Failed to load unread count:', response.message)
        setUnreadCount(0)
      }
    } catch (error) {
      console.error('Error loading unread count:', error)
      setUnreadCount(0)
    }
  }

  const loadRecentNotifications = async (userId) => {
    if (isLoadingNotifications) return

    setIsLoadingNotifications(true)
    try {
      console.log('Loading recent notifications for user:', userId)
      const response = await fetchNotifications(userId, false, 5) // Get 5 most recent notifications
      console.log('Recent notifications response:', response)
      console.log('Response data type:', typeof response.data)
      console.log('Response data:', response.data)

      if (response.status === 'success') {
        const notifications = Array.isArray(response.data) ? response.data : []
        console.log('Setting notifications:', notifications)
        setNotifications(notifications)
      } else {
        console.error('Failed to load notifications:', response.message)
        setNotifications([])
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
      console.error('Error details:', error.stack)
      setNotifications([])
    } finally {
      setIsLoadingNotifications(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleProfileClick = () => {
    navigate('/members/my-profile')
  }

  const handleNotificationItemClick = async (notification) => {
    console.log('Notification clicked:', notification)

    // Navigate to action URL if available
    if (notification.action_url) {
      console.log('Navigating to:', notification.action_url)
      navigate(notification.action_url)
    }

    // Close dropdown
    setShowNotificationDropdown(false)
  }

  const handleMarkAsRead = async (notification, e) => {
    e.stopPropagation() // Prevent triggering the main click handler

    if (!user?.id) return

    try {
      console.log('Marking notification as read:', notification.id)
      const response = await markNotificationAsRead(notification.id, user.id)
      console.log('Mark as read response:', response)

      if (response.status === 'success') {
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        )
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1))
        console.log('Notification marked as read successfully')
      } else {
        console.error('Failed to mark notification as read:', response.message)
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const handleMarkAsUnread = async (notification, e) => {
    e.stopPropagation() // Prevent triggering the main click handler

    if (!user?.id) return

    try {
      console.log('Marking notification as unread:', notification.id)
      const response = await markNotificationAsUnread(notification.id, user.id)
      console.log('Mark as unread response:', response)

      if (response.status === 'success') {
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: false } : n)
        )
        // Update unread count
        setUnreadCount(prev => prev + 1)
        console.log('Notification marked as unread successfully')
      } else {
        console.error('Failed to mark notification as unread:', response.message)
      }
    } catch (error) {
      console.error('Error marking notification as unread:', error)
    }
  }

  const handleViewAllNotifications = () => {
    navigate('/members/notifications')
    setShowNotificationDropdown(false)
  }

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return

    try {
      console.log('Marking all notifications as read')
      const response = await markAllNotificationsAsRead(user.id)
      console.log('Mark all as read response:', response)

      if (response.status === 'success') {
        // Update local state
        setNotifications(prev =>
          prev.map(n => ({ ...n, is_read: true }))
        )
        // Update unread count
        setUnreadCount(0)
        console.log('All notifications marked as read successfully')

        // Refresh the unread count to make sure it's accurate
        setTimeout(() => {
          loadUnreadCount(user.id)
        }, 500)
      } else {
        console.error('Failed to mark all notifications as read:', response.message)
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const handleMarkAllAsUnread = async () => {
    if (!user?.id) return

    try {
      console.log('Marking all notifications as unread')
      const response = await markAllNotificationsAsUnread(user.id)
      console.log('Mark all as unread response:', response)

      if (response.status === 'success') {
        // Update local state
        setNotifications(prev =>
          prev.map(n => ({ ...n, is_read: false }))
        )
        // Update unread count to total notifications
        setUnreadCount(notifications.length)
        console.log('All notifications marked as unread successfully')
      } else {
        console.error('Failed to mark all notifications as unread:', response.message)
      }
    } catch (error) {
      console.error('Error marking all notifications as unread:', error)
    }
  }

  // Refresh unread count periodically
  useEffect(() => {
    if (!user?.id) {
      console.log('No user ID for periodic refresh')
      return
    }

    console.log('Setting up periodic unread count refresh for user:', user.id)
    const interval = setInterval(() => {
      console.log('Periodic refresh of unread count')
      loadUnreadCount(user.id)
    }, 30000) // Refresh every 30 seconds

    return () => {
      console.log('Cleaning up periodic refresh interval')
      clearInterval(interval)
    }
  }, [user?.id])

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase() || 'U'
  }

  const getDisplayName = (userObj) => {
    console.log('Getting display name for user:', userObj)

    if (userObj?.full_name) {
      return userObj.full_name
    }

    if (userObj?.firstName && userObj?.lastName) {
      return `${userObj.firstName} ${userObj.lastName}`
    }

    if (userObj?.first_name && userObj?.last_name) {
      return `${userObj.first_name} ${userObj.last_name}`
    }

    if (userObj?.firstName) {
      return userObj.firstName
    }

    if (userObj?.first_name) {
      return userObj.first_name
    }

    if (userObj?.lastName) {
      return userObj.lastName
    }

    if (userObj?.last_name) {
      return userObj.last_name
    }

    if (userObj?.name) {
      return userObj.name
    }

    if (userObj?.email) {
      return userObj.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    return 'Member'
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'welcome':
        return cilStar
      case 'profile_update':
        return cilUser
      case 'password_change':
        return cilLockLocked
      case 'warning':
        return cilClock
      case 'success':
        return cilCheckCircle
      case 'system':
        return cilBell
      default:
        return cilBell
    }
  }

  const getNotificationColor = (type) => {
    switch (type) {
      case 'welcome':
        return 'primary'
      case 'profile_update':
        return 'info'
      case 'password_change':
        return 'warning'
      case 'warning':
        return 'danger'
      case 'success':
        return 'success'
      case 'system':
        return 'secondary'
      default:
        return 'info'
    }
  }

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffMinutes = Math.floor(diffTime / (1000 * 60))
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`
    } else if (diffHours < 24) {
      return `${diffHours}h ago`
    } else if (diffDays < 7) {
      return `${diffDays}d ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  return (
    <>
      {/* Main Header */}
      <CHeader position="sticky" className={`${theme === 'light' ? 'bg-white' : 'bg-dark'} border-bottom`}>
        <CContainer fluid className="px-4">
          {/* Left Section - Hamburger + Navigation */}
          <div className="d-flex align-items-center">
            <CHeaderToggler className="me-3" />


          </div>

          {/* Right Section - Icons */}
          <CHeaderNav className="ms-auto d-flex align-items-center">
            {/* Notifications Dropdown */}
            <CDropdown
              variant="nav-item"
              className="me-3"
              visible={showNotificationDropdown}
              onVisibleChange={(visible) => {
                console.log('Notification dropdown visibility changed:', visible)
                setShowNotificationDropdown(visible)
                if (visible && user?.id) {
                  console.log('Loading notifications for dropdown')
                  loadRecentNotifications(user.id)
                }
              }}
            >
              <CDropdownToggle
                className="position-relative border-0 bg-transparent p-0"
                caret={false}
                style={{ cursor: 'pointer' }}
                title="View notifications"
              >
                <CIcon icon={cilBell} size="lg" className="text-secondary" />
                {unreadCount > 0 && (
                  <CBadge
                    color="danger"
                    className="position-absolute top-0 start-100 translate-middle rounded-pill"
                    style={{ fontSize: '10px', minWidth: '18px' }}
                  >
                    {unreadCount}
                  </CBadge>
                )}
              </CDropdownToggle>
              <CDropdownMenu
                className="notification-dropdown"
                style={{
                  minWidth: '350px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  right: 0,
                  left: 'auto'
                }}
                placement="bottom-end"
              >
                <CDropdownHeader className="bg-light fw-semibold py-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <CBadge color="danger" className="small">
                        {unreadCount} new
                      </CBadge>
                    )}
                  </div>
                  {(notifications.length > 0 || unreadCount > 0) && (
                    <div className="d-flex gap-1">
                      <CButton
                        size="sm"
                        color="primary"
                        variant="outline"
                        onClick={handleMarkAllAsRead}
                        className="flex-fill"
                        style={{ fontSize: '11px', padding: '2px 6px' }}
                      >
                        <CIcon icon={cilCheckCircle} size="sm" className="me-1" />
                        Mark All Read
                      </CButton>
                      {notifications.length > 0 && (
                        <CButton
                          size="sm"
                          color="secondary"
                          variant="outline"
                          onClick={handleMarkAllAsUnread}
                          className="flex-fill"
                          style={{ fontSize: '11px', padding: '2px 6px' }}
                        >
                          <CIcon icon={cilBell} size="sm" className="me-1" />
                          Mark All Unread
                        </CButton>
                      )}
                    </div>
                  )}
                </CDropdownHeader>

                {isLoadingNotifications ? (
                  <div className="text-center py-3">
                    <CSpinner size="sm" />
                    <div className="small text-muted mt-1">Loading...</div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-4">
                    <CIcon icon={cilBell} className="text-muted mb-2" />
                    {unreadCount > 0 ? (
                      <div>
                        <div className="small text-muted mb-2">
                          {unreadCount} unread notification{unreadCount > 1 ? 's' : ''} found
                        </div>
                        <div className="small text-muted mb-3">
                          Unable to load notification details
                        </div>
                        <div className="d-flex gap-2 justify-content-center">
                          <CButton
                            size="sm"
                            color="primary"
                            variant="outline"
                            onClick={handleMarkAllAsRead}
                          >
                            <CIcon icon={cilCheckCircle} size="sm" className="me-1" />
                            Clear All
                          </CButton>
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="outline"
                            onClick={handleViewAllNotifications}
                          >
                            View Page
                          </CButton>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="small text-muted">No notifications yet</div>
                        <div className="mt-2">
                          <small
                            className="text-primary"
                            style={{ cursor: 'pointer' }}
                            onClick={handleViewAllNotifications}
                          >
                            View notifications page
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {notifications.map((notification) => (
                      <CDropdownItem
                        key={notification.id}
                        className={`notification-item border-0 ${!notification.is_read ? 'bg-light' : ''}`}
                        style={{
                          cursor: 'pointer',
                          whiteSpace: 'normal',
                          maxWidth: 'none',
                          padding: '12px 16px',
                          borderLeft: !notification.is_read ? '4px solid #0d6efd' : '4px solid transparent'
                        }}
                        onClick={() => handleNotificationItemClick(notification)}
                      >
                        <div className="d-flex align-items-start">
                          <div className="me-2">
                            <CBadge
                              color={getNotificationColor(notification.type)}
                              className="rounded-circle p-1"
                              style={{ width: '28px', height: '28px', fontSize: '12px' }}
                            >
                              <CIcon icon={getNotificationIcon(notification.type)} />
                            </CBadge>
                          </div>
                          <div className="flex-grow-1 min-width-0">
                            <div className="fw-semibold small mb-1 text-truncate">
                              {notification.title}
                            </div>
                            <div className="text-muted small mb-1" style={{ lineHeight: '1.3' }}>
                              {notification.message.length > 80
                                ? `${notification.message.substring(0, 80)}...`
                                : notification.message
                              }
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                {formatTimeAgo(notification.created_at)}
                              </small>
                              <div className="d-flex align-items-center gap-2">
                                {!notification.is_read && (
                                  <CBadge color="primary" className="small">
                                    New
                                  </CBadge>
                                )}
                                <CButtonGroup size="sm">
                                  {!notification.is_read ? (
                                    <CButton
                                      size="sm"
                                      color="primary"
                                      variant="outline"
                                      className="py-0 px-1"
                                      onClick={(e) => handleMarkAsRead(notification, e)}
                                      title="Mark as read"
                                    >
                                      <CIcon icon={cilCheckCircle} size="sm" />
                                    </CButton>
                                  ) : (
                                    <CButton
                                      size="sm"
                                      color="secondary"
                                      variant="outline"
                                      className="py-0 px-1"
                                      onClick={(e) => handleMarkAsUnread(notification, e)}
                                      title="Mark as unread"
                                    >
                                      <CIcon icon={cilBell} size="sm" />
                                    </CButton>
                                  )}
                                </CButtonGroup>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CDropdownItem>
                    ))}

                    <CDropdownDivider />
                    <CDropdownItem
                      className="text-center text-primary fw-semibold"
                      onClick={handleViewAllNotifications}
                      style={{ cursor: 'pointer' }}
                    >
                      View All Notifications
                    </CDropdownItem>
                  </>
                )}
              </CDropdownMenu>
            </CDropdown>



            {/* Theme Toggle Button */}
            <CNavItem className="me-3">
              <button
                className="theme-toggle-btn"
                onClick={toggleTheme}
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                <CIcon
                  icon={cilSun}
                  size="lg"
                  className="icon-light text-warning"
                />
                <CIcon
                  icon={cilMoon}
                  size="lg"
                  className="icon-dark text-primary"
                />
              </button>
            </CNavItem>

            {/* User Avatar Dropdown */}
            <CDropdown variant="nav-item">
              <CDropdownToggle placement="bottom-end" className="py-0 border-0" caret={false}>
                <CAvatar
                  src={user?.avatar}
                  size="md"
                  className="bg-primary text-white"
                >
                  {getInitials(getDisplayName(user))}
                </CAvatar>
              </CDropdownToggle>
              <CDropdownMenu className="pt-0">
                <CDropdownHeader className="bg-light fw-semibold py-2">
                  {getDisplayName(user)}
                </CDropdownHeader>
                <CDropdownItem onClick={handleProfileClick}>
                  <CIcon icon={cilUser} className="me-2" />
                  Profile
                </CDropdownItem>

                <CDropdownDivider />
                <CDropdownItem onClick={handleLogout}>
                  <CIcon icon={cilAccountLogout} className="me-2" />
                  Logout
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
          </CHeaderNav>
        </CContainer>
      </CHeader>

      {/* Breadcrumb Section - Sticky with conditional background */}
      <div className={`position-sticky ${theme === 'light' ? 'bg-white' : 'bg-dark'} border-bottom px-4 py-3`} style={{ top: '60px', zIndex: 1019 }}>
        <DynamicBreadcrumb />
      </div>
    </>
  )
}

export default MembersHeader 